import React, { memo, useEffect, useRef, useState } from "react";
import { Animated, Pressable, Text, View } from "react-native";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useIsFocused } from "@react-navigation/native";
import { useCssStyle } from "../../../customHook/getCssStyle";
import { getCleanUrl } from "../../../utils/Helper";
import { myStayTab, notificationKey, tabphone } from "../../../config/Constant";
import { setRedirectToTabId } from "../../../Redux/Reducers/appConfigReducer";
import { ImageComp } from "../../../components/Image/Image";


const TabBar_Portrait = memo(({ state, navigation, handleFooterNavClick }) => {

    const getStyles = useCssStyle();
    const dispatch = useDispatch();
    const isFocused = useIsFocused();

    // Combine selectors to reduce re-renders
    const {
        isLoggedIn,
        redirectToTabId,
        isBookingFound,
        currentLayout,
        notifications,
        newMessagesCount,
        mwiStatus
    } = useSelector(state => ({
        isLoggedIn: state.appConfigSlice.isLoggedIn,
        redirectToTabId: state.appConfigSlice.redirectToTabId,
        isBookingFound: state.checkInSlice.bookingStatus.isBookingFound,
        currentLayout: state.deviceConfigSlice.currentLayout,
        notifications: state.notificationSlice.notifications,
        newMessagesCount: state.notificationSlice.newMessagesCount,
        mwiStatus: state.softphoneSlice.mwiStatus,
    }), shallowEqual);

    const tabDetails = currentLayout?.tabDetails || [];

    const [tabBarWidth, setTabBarWidth] = useState(0);
    const tabWidth = tabBarWidth / state.routes.length;

    const translateX = useRef(new Animated.Value(0)).current;



    const getDefaultFooter = () => {
        let requiredTabIndex = 0;

        if (tabDetails?.length > 0) {
            if (redirectToTabId) {
                // Find the tab with the uniqueKey that matches initialTabId
                const requiredTabItem = tabDetails.findIndex(tabItem =>
                    redirectToTabId.includes(tabItem.uniqueKey.replace(/ /g, '').toLowerCase())
                );
                if (requiredTabItem >= 0) {
                    requiredTabIndex = requiredTabItem;
                }
            } else if (isLoggedIn || isBookingFound) {
                // Find the "my stay" tab
                const myStayTabItem = tabDetails.findIndex(tabItem =>
                    myStayTab.includes(tabItem.uniqueKey.replace(/ /g, '').toLowerCase())
                );

                if (myStayTabItem >= 0) {
                    requiredTabIndex = myStayTabItem;
                }
            }
            
            
            if(state.index != requiredTabIndex){
                setTimeout(() => {
                    handleFooterNavClick(tabDetails[requiredTabIndex], navigation)
                }, 0);
            }

        }
    };

    useEffect(() => {

        if (redirectToTabId) {
            getDefaultFooter();
            dispatch(setRedirectToTabId(null));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [redirectToTabId])

    useEffect(() => {
        // select required footer tab
        if (isLoggedIn || (isBookingFound && isFocused)) {
            getDefaultFooter();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, isBookingFound]);

    useEffect(() => {
        if (tabBarWidth > 0) {
            // Accomodate for tab bar padding as we do absolute positioning.
            const { paddingLeft, paddingRight } = getStyles(["tabBarStyle"]);
            Animated.spring(translateX, {
                toValue: state.index * tabWidth,
                useNativeDriver: false,
            }).start();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.index, tabBarWidth]);

    return (
        <View style={getStyles(["tabBarStyle"])}
            onLayout={(event) => {
                const { width } = event.nativeEvent.layout;
                // Accomodate for a tab bar narrower than the screen as we do absolute positioning.
                const { paddingLeft, paddingRight } = getStyles(["tabBarStyle"]);
                setTabBarWidth(width - paddingLeft - paddingRight);
            }}
            id="tabBarStyle"
        >

            <View style={[getStyles(["tabParentContainer"]), { flex: 1, flexDirection: "row" }]} id="tabParentContainer">

                {/* Sliding Indicator */}
                <Animated.View
                    style={[
                        getStyles(["tabContainerTopBorder_container"]),
                        {
                            width: tabWidth,
                            transform: [{ translateX: translateX }],
                        },
                    ]}
                    id={"tabContainerTopBorder_container"}
                >
                    <View style={getStyles(["tabContainerTopBorder"])} />
                </Animated.View>

                {state.routes.map((route, index) => {
                    const tab = tabDetails.find((tab) => tab.uniqueKey === route.name);
                    const isFocused = state.index === index;

                    let newPing = 0;
                    let oldPing = 0;


                    if(notificationKey.includes(tab.uniqueKey)){
                        newPing = newMessagesCount || 0;
                        oldPing = notifications?.length || 0;
                    }

                    if (tabphone.includes(tab.uniqueKey)) {
                        newPing = mwiStatus?.nbNewMessages || 0;
                        oldPing = mwiStatus?.nbOldMessages || 0;                          
                    }

                    return (
                        <Pressable
                            key={route.key}
                            onPress={() => handleFooterNavClick(tab, navigation)}
                            style={({ pressed }) =>
                                getStyles([
                                    "tabContainer",
                                    isFocused && "tabFocusedContainer",
                                    {
                                        opacity: pressed ? 0.7 : 1,
                                        transform: [{ scale: pressed ? 0.95 : 1 }],
                                    },
                                ])
                            }
                            id={`tabContainer tabContainer_${route.name}`}
                        >
                            <View style={getStyles(["tabIconContainer_portrait"])} id={`tabIconContainer_portrait_${tab.uniqueKey}`}>
                                <ImageComp
                                    source={isFocused && tab.tabFocusedIconUrl ? getCleanUrl(tab.tabFocusedIconUrl) : getCleanUrl(tab?.tabIconUrl)}
                                    style={getStyles(["tabIcon", `tabIcon_${route.name}`, isFocused && "tabIconFocused"])}
                                    id={`tabIcon tabIcon_${route.name}`}
                                />
                                {newPing > 0 && (
                                    <View style={getStyles(['notificationicon_countcontainer_unread'])}>
                                        <Text style={getStyles(['notificationicon_countcontainer_unread_text'])}>{newPing}</Text>
                                    </View>
                                )}
                                {newPing < 1 && oldPing > 0 && (
                                    <View style={getStyles(['notificationicon_countcontainer'])}>
                                        <Text style={getStyles(['notificationicon_countcontainer_text'])}>{oldPing}</Text>
                                    </View>
                                )}
                            </View>
                            <Text style={getStyles(["tabLabel", isFocused && "tabLabelFocused"])} id="tabLabel">
                                {tab?.tabName}
                            </Text>
                        </Pressable>
                    );
                })}
            </View>
        </View>
    );
});


export default TabBar_Portrait;